import React, { useEffect, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useActionHook } from "hooks";
import { useUserActions } from "store";

import { getInitials } from "./bookmark-page";
import { AVAILABLE_ROLES_FOR_BOOKMARK } from "./bookmark-page";

interface Props {
  initialSelectedIds: number[];
  doneLabel?: string;
  onSubmit: (selectedIds: number[]) => void;
  onCancel: () => void;
}

export const PeoplePicker: React.FC<Props> = ({ initialSelectedIds, doneLabel = "Add", onSubmit, onCancel }) => {
  const { getUsersByRoleAction } = useUserActions();
  const {
    action: fetchUsers,
    data: users = [],
    loading
  } = useActionHook((role_ids: number[]) => getUsersByRoleAction(role_ids));

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(initialSelectedIds));

  useEffect(() => {
    fetchUsers(AVAILABLE_ROLES_FOR_BOOKMARK);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.full_name || "").toLowerCase();
      return name.includes(q);
    });
  }, [users, search]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = selectedIds.size;

  return (
    <Stack
      gap="20px"
      sx={{
        width: "360px",
        maxWidth: "calc(100vw - 40px)",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 10px 15px -3px rgba(18,18,23,.08), 0px 4px 6px -2px rgba(18,18,23,.05)",
        padding: "24px",
        outline: "none"
      }}
    >
      <Stack>
        <Box sx={{ padding: "12px" }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search value"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "16px", color: "#496682" }} />
                </InputAdornment>
              )
            }}
            inputProps={{ style: { fontSize: "14px" } }}
            sx={{
              "& .MuiOutlinedInput-root": { height: "32px" },
              "& .MuiOutlinedInput-input": { paddingTop: 0, paddingBottom: 0 }
            }}
          />
        </Box>

        <Box sx={{ height: "264px", overflowY: "auto", borderBottom: "1px solid #dbe8f5", minHeight: 0 }}>
          {loading && (
            <Stack alignItems="center" justifyContent="center" sx={{ padding: "20px" }}>
              <CircularProgress size={20} sx={{ color: "#496682" }} />
            </Stack>
          )}
          {!loading && filtered.length === 0 && (
            <Typography sx={{ padding: "16px 12px", textAlign: "center", fontSize: "14px", color: "#496682" }}>
              No team members found.
            </Typography>
          )}
          {!loading &&
            filtered.map((user) => {
              const checked = selectedIds.has(user.id);
              return (
                <Stack
                  key={user.id}
                  component="label"
                  direction="row"
                  alignItems="center"
                  gap="8px"
                  sx={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#ecf1f8" }
                  }}
                >
                  <Checkbox checked={checked} onChange={() => toggle(user.id)} sx={{ padding: 0 }} />
                  <Avatar
                    src={user.profile_photo_url || undefined}
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: "14px",
                      fontWeight: 500,
                      backgroundColor: "#ecf1f8",
                      color: "#496682"
                    }}
                  >
                    {getInitials(user.full_name)}
                  </Avatar>
                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "16px", color: "#071f35", lineHeight: "24px" }} noWrap>
                      {user.full_name}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" gap="8px">
        <Typography sx={{ flex: 1, fontSize: "15px", color: "#496682", lineHeight: "20px" }}>
          {count} selected
        </Typography>
        <Stack direction="row" gap="8px">
          <Button onClick={onCancel} variant="outlined" sx={cancelBtnSx}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(Array.from(selectedIds))}
            disabled={count === 0}
            variant="contained"
            sx={addBtnSx}
          >
            {doneLabel}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const cancelBtnSx = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "24px",
  padding: "4px 16px",
  borderRadius: "32px",
  color: "#071f35",
  borderColor: "#071f35",
  "&:hover": { borderColor: "#071f35", backgroundColor: "#f6f8fc" }
};

const addBtnSx = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "24px",
  padding: "4px 16px",
  borderRadius: "32px",
  backgroundColor: "#071f35",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#0d2c4a", boxShadow: "none" }
};
